import React, { useState, useRef, useCallback, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView } from 'react-native';
import ReactDOM from 'react-dom';
import Svg, { Path } from 'react-native-svg';
import { FONTS } from '../constants/config';
import { useTheme } from '../contexts/ThemeContext';

/**
 * Single-select dropdown — same chip-style design as MultiSelectDropdown
 * but selects ONE value. No checkboxes; clicking an option chooses it
 * and closes the menu.
 */

export interface SingleSelectDropdownOption {
  value: string;
  label: string;
}

interface SingleSelectDropdownProps {
  value: string;
  options: SingleSelectDropdownOption[];
  placeholder: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  allowClear?: boolean;
}

const ChevronDownIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M6 9L12 15L18 9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

export default function SingleSelectDropdown({
  value,
  options,
  placeholder,
  onChange,
  disabled,
  allowClear = true,
}: SingleSelectDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [buttonLayout, setButtonLayout] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const buttonRef = useRef<View>(null);
  const { colors, isDark } = useTheme();

  const selectedOption = options.find(opt => opt.value === value) || null;

  const dropdownTheme = {
    bg: colors.card,
    border: colors.border,
    text: colors.textPrimary,
    placeholder: isDark ? '#6B7280' : '#9CA3AF',
    icon: colors.textMuted,
    disabledBg: isDark ? colors.backgroundAlt : '#F1F5F9',
    menuBg: colors.card,
    menuBorder: colors.border,
    itemBorder: isDark ? colors.border : '#F1F5F9',
    itemHover: isDark ? colors.backgroundAlt : '#F8FAFC',
    itemSelected: isDark ? 'rgba(169, 33, 31, 0.15)' : '#FEF2F2',
  };

  const handleOpen = () => {
    if (disabled) return;
    buttonRef.current?.measureInWindow((x: number, y: number, width: number, height: number) => {
      setButtonLayout({ x, y, width, height });
      setIsOpen(true);
    });
  };

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  const handleClear = (e: any) => {
    e?.stopPropagation?.();
    onChange('');
  };

  const menuDomRef = useRef<HTMLDivElement | null>(null);
  const menuCallbackRef = useCallback((node: any) => {
    menuDomRef.current = node as HTMLDivElement | null;
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const menuEl = menuDomRef.current;
      if (menuEl && menuEl.contains(e.target as Node)) return;
      setIsOpen(false);
    };
    const handleScroll = (e: Event) => {
      const menuEl = menuDomRef.current;
      if (menuEl && menuEl.contains(e.target as Node)) return;
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  const portalContent = isOpen ? ReactDOM.createPortal(
    <View
      ref={menuCallbackRef as any}
      style={{
        position: 'fixed' as any,
        top: buttonLayout.y + buttonLayout.height + 4,
        left: buttonLayout.x,
        width: buttonLayout.width,
        backgroundColor: dropdownTheme.menuBg,
        borderWidth: 1,
        borderColor: dropdownTheme.menuBorder,
        borderRadius: 12,
        maxHeight: 250,
        zIndex: 99999,
        elevation: 99999,
        overflow: 'hidden' as any,
      }}
      // @ts-ignore - web style
      onMouseDown={(e: any) => e.stopPropagation()}
    >
      <ScrollView style={{ maxHeight: 240 }} nestedScrollEnabled>
        <View style={{ boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.2)' } as any}>
          {options.length === 0 ? (
            <View style={{ paddingVertical: 16, alignItems: 'center' }}>
              <Text style={{ color: dropdownTheme.placeholder, fontSize: 13, fontFamily: FONTS.regular }}>
                لا توجد خيارات
              </Text>
            </View>
          ) : options.map((option) => {
            const isSelected = option.value === value;
            return (
              <TouchableOpacity
                key={option.value}
                style={[
                  styles.dropdownItem,
                  {
                    borderBottomColor: dropdownTheme.itemBorder,
                    backgroundColor: isSelected ? dropdownTheme.itemSelected : 'transparent',
                  },
                ]}
                onPress={() => handleSelect(option.value)}
              >
                <Text
                  style={[
                    styles.dropdownItemText,
                    {
                      color: isSelected ? '#A9211F' : dropdownTheme.text,
                      fontFamily: isSelected ? FONTS.medium : FONTS.regular,
                    },
                  ]}
                >
                  {isSelected ? '✓ ' : ''}{option.label}
                </Text>
              </TouchableOpacity>
            );
          })}
        </View>
      </ScrollView>
    </View>,
    document.body
  ) : null;

  return (
    <View style={styles.dropdownContainer}>
      <TouchableOpacity
        ref={buttonRef as any}
        style={[
          styles.dropdownButton,
          { backgroundColor: dropdownTheme.bg, borderColor: dropdownTheme.border },
          disabled && { backgroundColor: dropdownTheme.disabledBg }
        ]}
        onPress={handleOpen}
        disabled={disabled}
      >
        <ChevronDownIcon size={16} color={dropdownTheme.icon} />
        {!selectedOption ? (
          <Text style={[styles.dropdownButtonText, { color: dropdownTheme.placeholder }]}>
            {placeholder}
          </Text>
        ) : (
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, flex: 1, justifyContent: 'flex-end' }}>
            {allowClear && !disabled && (
              <TouchableOpacity onPress={handleClear} hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}>
                <Text style={{ color: '#EF4444', fontSize: 16, fontFamily: FONTS.regular }}>×</Text>
              </TouchableOpacity>
            )}
            <View style={{
              flexDirection: 'row', alignItems: 'center', gap: 4,
              backgroundColor: isDark ? '#374151' : '#F1F5F9',
              borderRadius: 6, paddingHorizontal: 10, paddingVertical: 4,
            }}>
              <Text style={{ color: dropdownTheme.text, fontSize: 13, fontFamily: FONTS.regular }}>
                {selectedOption.label}
              </Text>
            </View>
          </View>
        )}
      </TouchableOpacity>
      {portalContent}
    </View>
  );
}

const styles = StyleSheet.create({
  dropdownContainer: {
    position: 'relative',
    width: '100%',
  },
  dropdownButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    minHeight: 44,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
  },
  dropdownButtonText: {
    flex: 1,
    fontSize: 14,
    fontFamily: FONTS.regular,
    textAlign: 'right',
  },
  dropdownItem: {
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderBottomWidth: 1,
    cursor: 'pointer' as any,
  },
  dropdownItemText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    textAlign: 'right',
  },
});
